// Typed client for /admin-api/v1.
//
// Auth model: cookie-based (ssv2_sid, ssv2_csrf). The CSRF cookie is readable
// from JS; we mirror it in X-CSRF-Token on every mutating call. Same-origin
// requests so cookies attach automatically. In dev, Vite proxies /admin-api/*
// to the XAMPP host.
export class ApiException extends Error {
    code;
    status;
    constructor(err, status) {
        super(err.message);
        this.code = err.code;
        this.status = status;
    }
}
// API URL resolution. Two transports the build supports, picked at runtime:
//   1. CANONICAL — '<project-base>/admin-api/v1/<path>'. Pretty, but depends
//      on Apache mod_rewrite + .htaccess being honored on the host.
//   2. FALLBACK  — '<project-base>/admin-api.php?_route=<path>'. No rewrites
//      needed; works on shared hosts that ignore .htaccess.
// On startup we probe the canonical URL once. If the probe fails (404, HTML
// instead of JSON, network error), we lock in the fallback for the session.
// In dev mode the Vite proxy already routes /admin-api/* to XAMPP, so we
// always use canonical.
function detectProjectBase() {
    if (import.meta.env.DEV)
        return '';
    const m = window.location.pathname.match(/^(.*?)\/admin2(\/|$)/);
    return m ? m[1] : '';
}
const projectBase = detectProjectBase();
const buildCanonical = (path) => projectBase + '/admin-api/v1' + path;
const buildFallback = (path) => {
    const qIdx = path.indexOf('?');
    const route = qIdx >= 0 ? path.slice(0, qIdx) : path;
    const extras = qIdx >= 0 ? path.slice(qIdx + 1) : '';
    const params = new URLSearchParams(extras);
    params.set('_route', route);
    return projectBase + '/admin-api.php?' + params.toString();
};
let activeBuilder = buildCanonical;
let probePromise = null;
async function probeRewriteSupport() {
    if (import.meta.env.DEV)
        return;
    try {
        const r = await fetch(buildCanonical('/ping'), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        });
        if (r.ok) {
            const ct = r.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
                const j = await r.json().catch(() => null);
                if (j && j.ok)
                    return; // canonical works — keep it
            }
        }
    }
    catch {
        // network / CORS / other — fall through to fallback
    }
    activeBuilder = buildFallback;
}
function ensureBaseReady() {
    if (!probePromise)
        probePromise = probeRewriteSupport();
    return probePromise;
}
function buildApiUrl(path) {
    return activeBuilder(path);
}
// Same builder, exposed for components that build absolute URLs (e.g. CSV export download links).
function apiUrlPublic(path) {
    return activeBuilder(path);
}
function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
}
async function request(path, init = {}) {
    await ensureBaseReady();
    const method = (init.method ?? 'GET').toUpperCase();
    const headers = new Headers(init.headers ?? {});
    headers.set('Accept', 'application/json');
    const body = init.body;
    const browserSetsType = body instanceof FormData ||
        body instanceof URLSearchParams ||
        body instanceof Blob;
    if (body && !headers.has('Content-Type') && !browserSetsType) {
        headers.set('Content-Type', 'application/json');
    }
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
        const csrf = readCookie('ssv2_csrf');
        if (csrf)
            headers.set('X-CSRF-Token', csrf);
    }
    const res = await fetch(buildApiUrl(path), {
        ...init,
        method,
        headers,
        credentials: 'same-origin',
    });
    const data = (await res.json().catch(() => null));
    if (!data) {
        throw new ApiException({ code: 'BAD_RESPONSE', message: 'Invalid server response' }, res.status);
    }
    if (!data.ok) {
        throw new ApiException(data.error, res.status);
    }
    return data;
}
export const api = {
    ping: () => request('/ping'),
    login: (email, password) => request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
    }),
    logout: () => request('/auth/logout', { method: 'POST' }),
    me: () => request('/auth/me'),
    config: () => request('/config'),
    brand: () => request('/brand'),
    catalog: () => request('/catalog/blocks'),
    listPages: () => request('/pages'),
    getPage: (slug) => request(`/pages/${encodeURIComponent(slug)}`),
    getBlockSchemas: () => request('/block-schemas'),
    renderBlock: (slug, block, content) => request(`/pages/${encodeURIComponent(slug)}/render-block`, {
        method: 'POST',
        body: JSON.stringify(content === undefined ? { block } : { block, content }),
    }),
    /** Atomic save: one revision captures the entire draft. */
    savePage: (slug, payload, etag) => request(`/pages/${encodeURIComponent(slug)}`, {
        method: 'PUT',
        headers: etag ? { 'If-Match': etag } : undefined,
        body: JSON.stringify(payload),
    }),
    setBlockTree: (slug, blocks) => request(`/pages/${encodeURIComponent(slug)}/blocks/tree`, {
        method: 'PUT',
        body: JSON.stringify({ blocks }),
    }),
    insertBlock: (slug, payload) => request(`/pages/${encodeURIComponent(slug)}/blocks`, {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    updateBlock: (slug, id, patch) => request(`/pages/${encodeURIComponent(slug)}/blocks/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    }),
    deleteBlock: (slug, id) => request(`/pages/${encodeURIComponent(slug)}/blocks/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    }),
    duplicateBlock: (slug, id) => request(`/pages/${encodeURIComponent(slug)}/blocks/${encodeURIComponent(id)}/duplicate`, {
        method: 'POST',
    }),
    getContent: (slug, id) => request(`/pages/${encodeURIComponent(slug)}/blocks/${encodeURIComponent(id)}/html`),
    putContent: (slug, id, html) => request(`/pages/${encodeURIComponent(slug)}/blocks/${encodeURIComponent(id)}/html`, {
        method: 'PUT',
        body: JSON.stringify({ html }),
    }),
    undoPage: (slug) => request(`/pages/${encodeURIComponent(slug)}/undo`, { method: 'POST' }),
    redoPage: (slug) => request(`/pages/${encodeURIComponent(slug)}/redo`, { method: 'POST' }),
    pageRevisions: (slug) => request(`/pages/${encodeURIComponent(slug)}/revisions`),
    getSiteAssets: () => request('/site/assets'),
    putSiteAssets: (next) => request('/site/assets', { method: 'PUT', body: JSON.stringify(next) }),
    getPageAssets: (slug) => request(`/pages/${encodeURIComponent(slug)}/assets`),
    putPageAssets: (slug, next) => request(`/pages/${encodeURIComponent(slug)}/assets`, {
        method: 'PUT', body: JSON.stringify(next),
    }),
    createPage: (payload) => request('/pages', { method: 'POST', body: JSON.stringify(payload) }),
    patchPage: (slug, payload) => request(`/pages/${encodeURIComponent(slug)}`, {
        method: 'PATCH', body: JSON.stringify(payload),
    }),
    deletePage: (slug) => request(`/pages/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
    putBrand: (brand) => request('/brand', { method: 'PUT', body: JSON.stringify({ brand }) }),
    getLibraries: () => request('/libraries'),
    putLibraries: (libraries) => request('/libraries', {
        method: 'PUT', body: JSON.stringify({ libraries }),
    }),
    listUsers: () => request('/users'),
    createUser: (payload) => request('/users', {
        method: 'POST', body: JSON.stringify(payload),
    }),
    patchUser: (id, payload) => request(`/users/${id}`, {
        method: 'PATCH', body: JSON.stringify(payload),
    }),
    deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
    getSettings: () => request('/settings'),
    putSettings: (updates) => request('/settings', { method: 'PUT', body: JSON.stringify({ updates }) }),
    listMedia: () => request('/media'),
    uploadMedia: (file, ops) => {
        const fd = new FormData();
        fd.append('file', file);
        if (ops && Object.keys(ops).length > 0)
            fd.append('ops', JSON.stringify(ops));
        return request('/media', { method: 'POST', body: fd });
    },
    patchMedia: (rel, payload) => request(`/media/${rel.split('/').map(encodeURIComponent).join('/')}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    deleteMedia: (rel) => request(`/media/${rel.split('/').map(encodeURIComponent).join('/')}`, { method: 'DELETE' }),
    transformMedia: (rel, ops) => request(`/media/${rel.split('/').map(encodeURIComponent).join('/')}/transform`, { method: 'POST', body: JSON.stringify(ops) }),
    mediaUsages: (rel) => request(`/media/${rel.split('/').map(encodeURIComponent).join('/')}/usages`),
    mediaGc: (dry_run) => request('/media/gc', { method: 'POST', body: JSON.stringify({ dry_run }) }),
    // ---------- Audit + AI activity log (Phase J) ----------
    auditLog: (opts = {}) => {
        const q = new URLSearchParams();
        if (opts.limit !== undefined)
            q.set('limit', String(opts.limit));
        if (opts.offset !== undefined)
            q.set('offset', String(opts.offset));
        if (opts.action)
            q.set('action', opts.action);
        if (opts.user_id)
            q.set('user_id', String(opts.user_id));
        if (opts.since)
            q.set('since', opts.since);
        if (opts.until)
            q.set('until', opts.until);
        const qs = q.toString();
        return request(`/audit-log${qs ? '?' + qs : ''}`);
    },
    aiLog: (opts = {}) => {
        const q = new URLSearchParams();
        if (opts.limit !== undefined)
            q.set('limit', String(opts.limit));
        if (opts.offset !== undefined)
            q.set('offset', String(opts.offset));
        if (opts.provider)
            q.set('provider', opts.provider);
        if (opts.task)
            q.set('task', opts.task);
        if (opts.since)
            q.set('since', opts.since);
        if (opts.until)
            q.set('until', opts.until);
        const qs = q.toString();
        return request(`/ai-log${qs ? '?' + qs : ''}`);
    },
    // ---------- AI providers + task routing ----------
    getAiConfig: () => request('/ai/config'),
    putAiConfig: (payload) => request('/ai/config', { method: 'PUT', body: JSON.stringify(payload) }),
    testAiProvider: (provider, model) => request('/ai/test', { method: 'POST', body: JSON.stringify({ provider, model }) }),
    // ---------- SMTP / email ----------
    getEmailConfig: () => request('/email/config'),
    putEmailConfig: (payload) => request('/email/config', { method: 'PUT', body: JSON.stringify(payload) }),
    testEmail: () => request('/email/test', { method: 'POST' }),
    contentGc: (dry_run) => request('/content/gc', { method: 'POST', body: JSON.stringify({ dry_run }) }),
    // ---------- Dashboard ----------
    dashboardGlance: () => request('/dashboard/glance'),
    dashboardSubmissions: (limit = 5) => request(`/dashboard/submissions?limit=${limit}`),
    dashboardChecklist: () => request('/dashboard/checklist'),
    wizardState: () => request('/wizard/state'),
    wizardSetStep: (current_step) => request('/wizard/state', { method: 'PUT', body: JSON.stringify({ current_step }) }),
    wizardSaveNotes: (notes) => request('/wizard/state', { method: 'PUT', body: JSON.stringify({ notes }) }),
    wipeSite: (confirm_phrase) => request('/site/wipe', { method: 'POST', body: JSON.stringify({ confirm_phrase }) }),
    wizardInputsList: () => request('/wizard/inputs'),
    wizardInputsUpload: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return request('/wizard/inputs', { method: 'POST', body: fd });
    },
    wizardInputsDelete: (filename) => request(`/wizard/inputs/${encodeURIComponent(filename)}`, { method: 'DELETE' }),
    wizardInputsUrls: (urls) => request('/wizard/inputs/urls', { method: 'PUT', body: JSON.stringify({ urls }) }),
    wizardPublishState: () => request('/wizard/publish'),
    wizardPublish: (env) => request('/wizard/publish', {
        method: 'POST', body: JSON.stringify({ env }),
    }),
    wizardIngestPreview: () => request('/wizard/ingest'),
    wizardIngestRun: (apply, raw_text) => request('/wizard/ingest', {
        method: 'POST', body: JSON.stringify({ apply, raw_text: raw_text ?? '' }),
    }),
    wizardIngestSave: (file, content) => request(`/wizard/ingest/${encodeURIComponent(file)}`, {
        method: 'PUT', body: JSON.stringify({ content }),
    }),
    wizardPlanGet: () => request('/wizard/plan'),
    wizardPlanRun: (apply, instruction) => request('/wizard/plan', {
        method: 'POST', body: JSON.stringify({ apply, instruction: instruction ?? '' }),
    }),
    wizardPlanSave: (markdown) => request('/wizard/plan', { method: 'PUT', body: JSON.stringify({ markdown }) }),
    wizardSeoAudit: () => request('/wizard/seo'),
    wizardSeoApply: (fix_meta) => request('/wizard/seo', {
        method: 'POST', body: JSON.stringify({ fix_meta }),
    }),
    wizardBuildState: () => request('/wizard/build'),
    wizardPageOutline: (payload) => request('/wizard/page-outline', { method: 'POST', body: JSON.stringify(payload) }),
    wizardSection: (payload) => request('/wizard/section', { method: 'POST', body: JSON.stringify(payload) }),
    aiRewrite: (payload) => request('/ai/rewrite', { method: 'POST', body: JSON.stringify(payload) }),
    aiImagePrompt: (payload) => request('/ai/image-prompt', { method: 'POST', body: JSON.stringify(payload) }),
    aiSection: (payload) => request('/ai/section', { method: 'POST', body: JSON.stringify(payload) }),
    listForms: () => request('/forms'),
    getForm: (id) => request(`/forms/${encodeURIComponent(id)}`),
    createForm: (def) => request('/forms', { method: 'POST', body: JSON.stringify(def) }),
    updateForm: (id, def) => request(`/forms/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(def) }),
    deleteForm: (id) => request(`/forms/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    listSubmissions: (id, opts = {}) => {
        const q = new URLSearchParams();
        if (opts.limit !== undefined)
            q.set('limit', String(opts.limit));
        if (opts.offset !== undefined)
            q.set('offset', String(opts.offset));
        const qs = q.toString();
        return request(`/forms/${encodeURIComponent(id)}/submissions${qs ? '?' + qs : ''}`);
    },
    deleteSubmission: (id, subId) => request(`/forms/${encodeURIComponent(id)}/submissions/${subId}`, { method: 'DELETE' }),
    submissionsCsvUrl: (id) => apiUrlPublic(`/forms/${encodeURIComponent(id)}/submissions/export.csv`),
    // ---- Phase F2: test + retry + resend ----
    testSubmitForm: (id, values) => request(`/forms/${encodeURIComponent(id)}/test-submit`, { method: 'POST', body: JSON.stringify({ values }) }),
    testWebhook: (id) => request(`/forms/${encodeURIComponent(id)}/test-webhook`, { method: 'POST', body: JSON.stringify({}) }),
    retrySubmissionWebhook: (id, subId) => request(`/forms/${encodeURIComponent(id)}/submissions/${subId}/retry-webhook`, { method: 'POST' }),
    resendSubmissionEmail: (id, subId) => request(`/forms/${encodeURIComponent(id)}/submissions/${subId}/resend-email`, { method: 'POST' }),
};
